import { IWindowService } from 'angular';
import { IStateService } from 'angular-ui-router';
import * as _ from 'lodash';
import { ImSchool } from 'Src/ng2/shared/services/im-models/im-school';
// tslint:disable-next-line:max-line-length
import { CurrentGraduationThresholdAsOfMonth } from '../../../../ng2/shared/constants/current-graduation-threshold-as-of-month.constant';
import { NextRegentsAdminDate } from '../../../../ng2/shared/constants/next-regents-admin-date.constant';
import { IRegentsExam, RegentsExam } from '../../../../ng2/shared/constants/regents.constant';
import { ISchool } from '../../../../ng2/shared/typings/interfaces/school.interface';
import { IStudent } from '../../../../ng2/shared/typings/interfaces/student.interface';
import { SdcActionsService } from './../../../sdc/flux/sdc-actions.service';
import { ISdcStore, IStoreScope } from './../../../sdc/flux/sdc-store.interface';
import { StudentStatic } from './../../static-models/student-static.service';

type TIRegentsFields = keyof IRegentsExam;

export interface IRegentsBulkPatch {
  _ids: string[];
  path: string;
  newValue: string;
}

export interface IStudentToExamData {
  [key: string]: {
    _ids: string[];
    studentIds: string[];
  };
}

interface IComponentController {
  $scope: IStoreScope;
  setUi(): void;
  [x: string]: any;
}

export class WizardUtils {
  static $inject: string[] = [
    '$timeout',
    'Rollbar',
    '$mdDialog',
    'BackgroundJobSpinner',
    'SDCStore',
    'sdcActions',
    'ConfirmationModal',
    '$state',
    '$window',
    'ObjectCache',
    'ImSchool',
  ];

  constructor(
    private $timeout,
    private Rollbar,
    private $mdDialog,
    private BackgroundJobSpinner,
    private SDCStore: ISdcStore,
    private sdcActions: SdcActionsService,
    private ConfirmationModal,
    private $state: IStateService,
    private $window: IWindowService,
    private ObjectCache,
    private ImSchool: ImSchool,
  ) {}

  goToStateWithFilter(state: string, studentId: string[], options?): void {
    const filter = this.ObjectCache.cacheObject({ studentId });
    let sort = null;
    let gridView = null;
    if (options) {
      sort = options.sort ? this.ObjectCache.cacheObject([options.sort]) : null;
      gridView = options.gridView ? options.gridView : null;
    }
    this.$timeout(() => {
      this.$state.go(state, { filter, sort, gridView });
    });
  }

  goToStateWithFilterInNewTab(state: string, studentId: string[], options?): void {
    const filter = this.ObjectCache.cacheObject({ studentId });
    let sort = null;
    let gridView = null;
    if (options) {
      sort = options.sort ? this.ObjectCache.cacheObject([options.sort]) : null;
      gridView = options.gridView ? options.gridView : null;
    }
    const url = this.$state.href(state, { filter, sort, gridView });
    this.$window.open(url, '_blank');
  }

  goToStateInNewTab(state: string): void {
    const url = this.$state.href(state);
    this.$window.open(url);
  }

  getFilteredFlattenedStudents(filterObject?): any[] {
    return this.SDCStore.getFilteredFlattenedStudents(filterObject);
  }

  getFilteredUnflattenedStudents(): IStudent[] {
    return this.SDCStore.getFilteredStudents();
  }

  getAllFlattenedStudents(): any[] {
    return this.SDCStore.getAllFlattenedStudents();
  }

  getAllUnflattenedStudents(): IStudent[] {
    return this.SDCStore.getAllStudents();
  }

  getCurrentSdcSchool(): ISchool {
    return this.SDCStore.getSchool();
  }

  getEntireSdcFilter() {
    return this.SDCStore.getFilter();
  }

  getCurrentCohortFilter(): string[] {
    const { cohort } = this.SDCStore.getFilter();
    return cohort ? cohort : null;
  }

  getCurrentPlannedGradDateFilter() {
    const { plannedGraduationDate } = this.SDCStore.getFilter();
    return plannedGraduationDate ? plannedGraduationDate : null;
  }

  getHref(toEmail: string, subject: string): string {
    const base = `https://mail.google.com/mail/?view=cm`;
    const to = `&to=${encodeURIComponent(toEmail)}`;
    const sub = `&su=${encodeURIComponent(subject)}`;
    return `${base}${to}${sub}`;
  }

  getIdsFromDistrictIds(districtIds) {
    const { _id: schoolId } = this.SDCStore.getSchool();
    return StudentStatic.createIdsFromDistrictIds(schoolId, districtIds);
  }

  confirmThenPatchStudentExams(count: number, patches: IRegentsBulkPatch[], noun = 'exam'): void {
    const title = 'Confirm Bulk Update';
    const singularMessage = `This will bulk schedule ${count} ${noun}`;
    const finalMessage = count === 1 ? singularMessage : `${singularMessage}s`;
    const promise = this.ConfirmationModal.show(title, finalMessage);
    promise
      .then(
        () => {
          this.bulkPatchStudents(patches);
        },
        () => {
          this.$mdDialog.hide();
        },
      )
      .catch(err => {
        this.Rollbar.error(`Unable to bulk patch students`, err);
      });
  }

  confirmThenPatchStudentGradPlans(count: number, patches: IRegentsBulkPatch[], textOptions?): void {
    const title = 'Assign Graduation Plan';
    const text = `This action will assign Planned Diploma Type: ${textOptions.diplomaType}
      and Planned Graduation Date: ${textOptions.gradDate} to
      ${count} student(s).  This action will overwrite existing plans.`;
    const promise = this.ConfirmationModal.show(title, text, 'Assign');
    promise
      .then(
        () => {
          this.bulkPatchStudents(patches);
        },
        () => {
          this.$mdDialog.hide();
        },
      )
      .catch(err => {
        this.Rollbar.error(`Unable to bulk patch students`, err);
      });
  }

  getRegentsExamBulkPatch(
    studentToExamData: IStudentToExamData,
    IRegentExamField: TIRegentsFields,
  ): IRegentsBulkPatch[] {
    return _.reduce(
      studentToExamData,
      (patches: IRegentsBulkPatch[], { _ids }, prop: string) => {
        if (!_ids.length) return patches;
        const propToKey = _.find(RegentsExam, { [IRegentExamField]: prop }).key;
        patches.push({
          _ids,
          path: `nextScheduledRegents.${propToKey}.adminDate`,
          newValue: NextRegentsAdminDate,
        });
        return patches;
      },
      [],
    );
  }

  getGradPlanningBulkPatch(_ids, gradDate, diplomaType) {
    return [
      {
        _ids,
        path: 'gradPlanningDetails.plannedDiplomaType',
        newValue: `${diplomaType}`,
      },
      {
        _ids,
        path: 'gradPlanningDetails.plannedGraduationDate',
        newValue: `${gradDate}`,
      },
    ];
  }

  getGradPlanThresholdExplainer(diplomaType?, gradMonth?) {
    const school = this.getCurrentSdcSchool();
    const currentCohortFilter = _.last(this.getCurrentCohortFilter());
    return this.ImSchool.getGradPlanningThresholdsExplainer(
      school,
      currentCohortFilter,
      CurrentGraduationThresholdAsOfMonth,
      diplomaType,
      gradMonth,
    );
  }

  getCountOfStudentsToBePatched(studentExamData: IStudentToExamData): number {
    return _.reduce(
      studentExamData,
      (total: number, { studentIds }) => {
        return (total += studentIds.length);
      },
      0,
    );
  }

  getRegentsWizardLanguageHelper(preferredKey) {
    return _.reduce(
      RegentsExam,
      (helperObject, examData) => {
        const key = examData[preferredKey];
        // can add but not remove keys
        const { humanName, subject } = examData;
        helperObject[key] = { humanName, subject };
        return helperObject;
      },
      {},
    );
  }

  startListeningAndLoad(componentController: IComponentController): void {
    componentController.$scope.$listenTo(this.SDCStore, ['ui', 'isLoading'], () => {
      const isLoading = this.SDCStore.getUiState().isLoading;
      if (!isLoading) {
        componentController.setUi();
        this.setListeners(componentController);
      }
    });
  }

  bulkPatchStudents(patches: IRegentsBulkPatch[]): void {
    const backgroundJob = this.sdcActions.multiUpdateStudentAsync(patches);
    this.BackgroundJobSpinner.activateSpinner('Updating Students', backgroundJob);
  }

  private setListeners(componentController: IComponentController): void {
    componentController.$scope.$listenTo(
      this.SDCStore,
      ['filteredFlattenedStudents'],
      componentController.setUi.bind(componentController),
    );
    componentController.$scope.$listenTo(
      this.SDCStore,
      ['school'],
      componentController.setUi.bind(componentController),
    );
    componentController.$scope.$listenTo(
      this.SDCStore,
      ['schoolSupports'],
      componentController.setUi.bind(componentController),
    );
  }
}
